import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, HelpCircle, Info, CheckCircle2, RefreshCw, ArrowRight, ChevronRight } from 'lucide-react';
// --- CALCULATOR DATA ---
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french_drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [
// Base & Compaction
{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$52'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$52'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$50'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$35'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$35'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$16'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$58'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$38'
},
// Clean Drain Rock
{
  id: '58-clear',
  name: '5/8" Clean',
  density: 1.35,
  priceRange: '$70'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '125-clear',
  name: '1.25" Clean',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$54'
},
// Soil, Mulch & Organic
{
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$43'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$51'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$60'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$45'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$45'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$45'
},
// Landscape & Decor
{
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$76'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$84'
},
// Quarry Rock
{
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$125'
}];
// --- QUIZ DATA ---
const quizQuestions = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    label: 'Driveway or Parking Area',
    value: 'driveway'
  }, {
    label: 'Drainage (French drain, retaining wall)',
    value: 'drainage'
  }, {
    label: 'Landscaping or Garden Beds',
    value: 'landscape'
  }, {
    label: 'Walkway or Patio Base',
    value: 'walkway'
  }, {
    label: 'Erosion Control or Slope Stabilization',
    value: 'erosion'
  }, {
    label: 'Concrete, Masonry, or Leveling',
    value: 'concrete'
  }, {
    label: 'Grading, Fill, or Raising Low Spots',
    value: 'fill'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Clean',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Clean, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Clean provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold. Delivery includes placement.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
export function HelpMeChoose() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'calculator' | 'quiz'>(searchParams.get('tab') === 'calculator' ? 'calculator' : 'quiz');
  // Calculator State
  const [projectType, setProjectType] = useState(projectTypes[0].id);
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState(projectTypes[0].defaultDepth.toString());
  const [material, setMaterial] = useState(calcMaterials[0].id);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) setDepth(selectedProject.defaultDepth.toString());
  }, [projectType]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  // Calculate progress
  const totalSteps = 2; // Most paths are 2 steps
  const currentStep = quizResult ? 3 : Object.keys(quizAnswers).length + 1;
  const progressPercent = Math.min(currentStep / (totalSteps + 1) * 100, 100);
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) setQuizResult(result);
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const inputClass = 'w-full px-4 py-3 bg-stone-50 dark:bg-[#3a3a3a] border border-stone-200 dark:border-[#555555] rounded-xl text-sm text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange outline-none transition-all appearance-none';
  const tabs = [{
    id: 'quiz' as const,
    label: 'Material Quiz',
    icon: HelpCircle
  }, {
    id: 'calculator' as const,
    label: 'Calculator',
    icon: Calculator
  }];
  return <main className="py-12 md:py-20 bg-white dark:bg-[#373737] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-1696">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1697">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-1698">
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1699">
            Help Me Choose
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-brand-muted dark:text-stone-300 text-sm md:text-base max-w-lg mx-auto" data-id="element-1700">
            Not sure which material you need or how much? Take our quick quiz or
            use the calculator.
          </motion.p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12" data-id="element-1701">
          <div className="relative flex gap-8" data-id="element-1702">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center pb-2 text-sm font-semibold transition-colors duration-300 ${isActive ? 'text-brand-charcoal dark:text-white' : 'text-brand-muted dark:text-stone-300 hover:text-brand-charcoal dark:hover:text-white'}`} data-id="element-1703">
                  <tab.icon className="w-4 h-4 mr-2" data-id="element-1704" />
                  {tab.label}
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-orange rounded-full" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-1705" />}
                </button>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto" data-id="element-1706">
          <AnimatePresence mode="wait" data-id="element-1707">
            {/* QUIZ */}
            {activeTab === 'quiz' && <motion.div key="quiz" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-1708">
                <div className="bg-white dark:bg-[#474747] rounded-2xl border border-stone-100 dark:border-[#555555] shadow-sm overflow-hidden relative" data-id="element-1709">
                  {/* Progress Bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#555555] dark:bg-[#555555]" data-id="element-1710">
                    <motion.div className="h-full bg-brand-orange" initial={{
                  width: 0
                }} animate={{
                  width: `${progressPercent}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeInOut'
                }} data-id="element-1711" />
                  </div>

                  <div className="p-8 md:p-12" data-id="element-1712">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.4,
                  ease: [0.16, 1, 0.3, 1]
                }} className="text-center relative" data-id="element-1713">
                        <div className="w-12 h-12 bg-stone-50 dark:bg-[#3a3a3a] border border-stone-100 dark:border-[#555555] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1714">
                          <CheckCircle2 className="w-5 h-5 text-brand-orange" data-id="element-1715" />
                        </div>
                        <h3 className="text-xs font-semibold tracking-widest uppercase text-brand-muted dark:text-stone-400 mb-2" data-id="element-1716">
                          We Recommend
                        </h3>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4 tracking-tight" data-id="element-1717">
                          {quizResult.name}
                        </h2>
                        <p className="text-stone-500 dark:text-stone-300 mb-10 max-w-md mx-auto leading-relaxed text-[15px]" data-id="element-1718">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1719">
                          <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-2.5 bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal rounded-lg font-medium text-sm hover:bg-neutral-800 dark:hover:bg-stone-100 transition-colors" data-id="element-1720">
                            Browse Materials
                          </Link>
                          <button onClick={resetQuiz} className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-medium text-sm text-brand-charcoal dark:text-white bg-stone-50 dark:bg-[#3a3a3a] border border-stone-200 dark:border-[#555555] hover:bg-stone-100 dark:hover:bg-[#4a4a4a] transition-colors" data-id="element-1721">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-1722" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <AnimatePresence mode="wait" data-id="element-1723">
                        <motion.div key={currentQuizQuestion.id} initial={{
                    opacity: 0,
                    x: 10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -10
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeOut'
                  }} data-id="element-1724">
                          <h3 className="font-heading text-xl md:text-2xl font-semibold text-brand-charcoal dark:text-white mb-8 text-center tracking-tight" data-id="element-1725">
                            {currentQuizQuestion.question}
                          </h3>
                          <div className="space-y-2.5 max-w-lg mx-auto" data-id="element-1726">
                            {currentQuizQuestion.options.map((option, index) => <motion.button key={option.value} initial={{
                        opacity: 0,
                        y: 5
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: index * 0.03
                      }} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full flex items-center justify-between p-4 rounded-xl border border-stone-200 dark:border-[#555555] bg-white dark:bg-[#3a3a3a] hover:border-brand-orange/50 dark:hover:border-brand-orange/50 hover:bg-stone-50 dark:hover:bg-[#4a4a4a] transition-all group" data-id="element-1727">
                                  <span className="font-medium text-[15px] text-brand-charcoal dark:text-stone-200 group-hover:text-brand-orange transition-colors" data-id="element-1728">
                                    {option.label}
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-1729" />
                                </motion.button>)}
                          </div>
                        </motion.div>
                      </AnimatePresence> : null}
                  </div>
                </div>
              </motion.div>}

            {/* CALCULATOR */}
            {activeTab === 'calculator' && <motion.div key="calculator" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-1730">
                <div className="bg-white dark:bg-[#474747] rounded-2xl border border-stone-100 dark:border-[#555555] shadow-sm overflow-hidden relative" data-id="element-1731">
                  <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1732">
                    <div className="p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#555555]" data-id="element-1733">
                      <form onSubmit={handleCalculate} className="space-y-5" data-id="element-1734">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1735">
                          <div data-id="element-1736">
                            <label className="block text-xs font-bold text-brand-muted dark:text-stone-300 mb-1.5 uppercase tracking-wider" data-id="element-1737">
                              Project Type
                            </label>
                            <select value={projectType} onChange={e => setProjectType(e.target.value)} className={inputClass} data-id="element-1738">
                              {projectTypes.map(p => <option key={p.id} value={p.id} data-id="element-1739">
                                  {p.name}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1740">
                            <label className="block text-xs font-bold text-brand-muted dark:text-stone-300 mb-1.5 uppercase tracking-wider" data-id="element-1741">
                              Material
                            </label>
                            <select value={material} onChange={e => setMaterial(e.target.value)} className={inputClass} data-id="element-1742">
                              {calcMaterials.map(m => <option key={m.id} value={m.id} data-id="element-1743">
                                  {m.name}
                                </option>)}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4" data-id="element-1744">
                          <div data-id="element-1745">
                            <label className="block text-xs font-bold text-brand-muted dark:text-stone-300 mb-1.5 uppercase tracking-wider" data-id="element-1746">
                              Length{' '}
                              <span className="text-stone-400 dark:text-stone-400 font-normal normal-case" data-id="element-1747">
                                (ft)
                              </span>
                            </label>
                            <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1748" />
                          </div>
                          <div data-id="element-1749">
                            <label className="block text-xs font-bold text-brand-muted dark:text-stone-300 mb-1.5 uppercase tracking-wider" data-id="element-1750">
                              Width{' '}
                              <span className="text-stone-400 dark:text-stone-400 font-normal normal-case" data-id="element-1751">
                                (ft)
                              </span>
                            </label>
                            <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1752" />
                          </div>
                          <div data-id="element-1753">
                            <label className="block text-xs font-bold text-brand-muted dark:text-stone-300 mb-1.5 uppercase tracking-wider" data-id="element-1754">
                              Depth{' '}
                              <span className="text-stone-400 dark:text-stone-400 font-normal normal-case" data-id="element-1755">
                                (in)
                              </span>
                            </label>
                            <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className={inputClass} required min="0.1" step="any" data-id="element-1756" />
                          </div>
                        </div>

                        <div className="pt-2" data-id="element-1757">
                          <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center group" data-id="element-1758">
                            <Calculator className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" data-id="element-1759" />
                            Calculate Need
                          </button>
                        </div>
                      </form>
                    </div>

                    <div className="p-8 md:col-span-2 bg-stone-50 dark:bg-[#3a3a3a] flex flex-col justify-center relative overflow-hidden" data-id="element-1760">
                      <AnimatePresence mode="wait" data-id="element-1761">
                        {calcResults ? <motion.div key="results" initial={{
                      opacity: 0,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} className="space-y-8 relative z-10" data-id="element-1762">
                            <div data-id="element-1763">
                              <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1764">
                                Estimated Volume
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1765">
                                <span className="font-heading text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-1766">
                                  {calcResults.yards}
                                </span>
                                <span className="text-brand-muted dark:text-stone-300 font-medium" data-id="element-1767">
                                  Yards
                                </span>
                              </div>
                            </div>

                            <div data-id="element-1768">
                              <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1769">
                                Estimated Weight
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1770">
                                <span className="font-heading text-4xl font-bold text-brand-charcoal dark:text-white tracking-tight" data-id="element-1771">
                                  {calcResults.tons}
                                </span>
                                <span className="text-brand-muted dark:text-stone-300 font-medium" data-id="element-1772">
                                  Tons
                                </span>
                              </div>
                            </div>

                            <div className="pt-6 border-t border-stone-200 dark:border-[#555555]" data-id="element-1773">
                              <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1774">
                                Material Price Range
                              </p>
                              <p className="text-xl font-bold text-brand-charcoal dark:text-white" data-id="element-1775">
                                {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                                <span className="text-sm text-brand-muted dark:text-stone-400 font-normal" data-id="element-1776">
                                  / yard
                                </span>
                              </p>
                              <p className="text-xs text-brand-muted dark:text-stone-400 mt-2 flex items-start leading-relaxed" data-id="element-1777">
                                <Info className="w-3.5 h-3.5 mr-1.5 mt-0.5 shrink-0" data-id="element-1778" />
                                Delivery fee not included. Final price depends
                                on exact location.
                              </p>
                            </div>
                          </motion.div> : <motion.div key="empty" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} className="text-center text-brand-muted dark:text-stone-400 relative z-10" data-id="element-1779">
                            <div className="w-16 h-16 bg-stone-200 dark:bg-[#4a4a4a] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1780">
                              <Calculator className="w-8 h-8 opacity-50" data-id="element-1781" />
                            </div>
                            <p className="text-sm max-w-[200px] mx-auto leading-relaxed" data-id="element-1782">
                              Enter your project dimensions to see estimated
                              materials needed.
                            </p>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </main>;
}